import { IConfigurationRegistry } from "../../../../base/domain/configuration";
import { UuidFactory } from "../../../../base/utilities";
import {
  ExpressionKinds,
  IExpressionContainable,
  IExpressionRangeSpecifiable,
  ICalculationExpressionBehavior,
  IConditionalExpressionBehavior,
  IDateDurationExpressionBehavior,
  ITimeSpanExpressionBehavior,
  IDateComparisonExpressionBehavior,
  instanceOfIExpressionBehaviorSpecifiable,
  instanceOfITextJoinBehaviorSpecifiable,
  GraphKinds,
  ILineGraphBehavior,
  instanceOfITranscribeBehaviorSpecifiable,
  instanceOfIDateCalculateBehaviorSpecifiable,
  instanceOfIItemSelectTriggerBehaviorSpecifiable,
  instanceOfITranscribeDateOfTimeBehaviorSpecifiable,
  instanceOfIEmptyAlertAllowBehaviorSpecifiable,
  instanceOfIGroupAlertAllowBehaviorSpecifiable,
  instanceOfIScriptExecuteTriggerBehaviorSpecifiable,
  instanceOfIScriptExecuteBehaviorSpecifiable,
  ILinkedScriptExecuteBehavior,
  instanceOfIHospitalizedHisInformationItemSelectBehaviorSpecifiable,
} from "../../../../documaker/domain/format/objects";
import { IConfiguration } from "../../../domain/configuration";
import {
  IEditableFormatObject,
  IEditableFormatClipboardRepository,
} from "../../../domain/editableFormat";
import {
  instanceOfIGraphObject,
} from "../../../domain/editableFormat/objects";
import { TranslatorFactory } from "./translators";

export class EditableFormatClipboardRepository implements IEditableFormatClipboardRepository {
  constructor(private readonly _configurationRegistry: IConfigurationRegistry<IConfiguration>) {
    this._translatorFactory = new TranslatorFactory(this._configurationRegistry);
  }

  private readonly _translatorFactory: TranslatorFactory;
  private readonly uuid = UuidFactory.create();
  
  getClippedObjects(formatId: number): IEditableFormatObject[] {
    // ログインユーザが違ってもオブジェクトをコピーできてもいいのかは議論が必要だが、以下の理由からフォーマット編集としては許容する.(シンクラは別途検証要.)
    // ・用途的にフォーマットには個人情報は入らないと思われ、ガードしても何も守れそうにない.
    // ・実際にログインユーザを切り替えて運用するケースはほとんどないと思われる.
    // 他のアプリケーションで流用する場合は、よくよく考えて採用すること.
    const objects = this.readClippedObjects(formatId);
    this.correctClippedObjects(objects);
    return objects;
  }

  private readClippedObjects(formatId: number): IEditableFormatObject[] {
    const recorded = localStorage.getItem(EditableFormatClipboardRepository.clippedObjectsKey);
    return recorded
      ? this._translatorFactory.create().toFormatPageObjects(JSON.parse(recorded), formatId)
      : [];
  }

  private correctClippedObjects(objects: IEditableFormatObject[]): void {
    if (localStorage.getItem(EditableFormatClipboardRepository.objectsClipperUuid) !== this.uuid) {
      // フォーマット編集跨ぎの場合はオブジェクト参照は引き継がない.クリアする.
      objects.forEach(o => this.clearObjectReferences(o));
    }
  }

  private clearObjectReferences(object: IEditableFormatObject): void {
    this.clearExpressionBehaviorObjectReferences(object);
    this.clearTextJoinBehaviorObjectReferences(object);
    this.clearGraphBehaviorObjectReferences(object);
    this.clearTranscribeBehaviorObjectReferences(object);
    this.clearDateCalculateBehaviorObjectReferences(object);
    this.clearItemSelectTriggerBehaviorObjectReferences(object);
    this.clearTranscribeDateOfTimeBehaviorObjectReferences(object);
    this.clearEmptyAlertAllowBehaviorObjectReferences(object);
    this.clearGroupAlertAllowBehaviorObjectReferences(object);
    this.clearScriptExecuteTriggerBehaviorObjectReferences(object);
    this.clearScriptExecuteBehaviorObjectReferences(object);
    this.clearHospitalizedHisInformationItemSelectBehaviorObjectReferences(object);
  }

  private clearExpressionBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIExpressionBehaviorSpecifiable(object)) {
      return;
    }
    switch (object.expressionKind) {
      case ExpressionKinds.Calculation:
        this.clearCalculationExpressionBehaviorContents(object.expressionBehavior as ICalculationExpressionBehavior);
        break;
      case ExpressionKinds.Conditional:
        this.clearExpressionBehaviorContents(object.expressionBehavior as IConditionalExpressionBehavior);
        break;
      case ExpressionKinds.DateDuration:
        this.clearExpressionBehaviorRange(object.expressionBehavior as IDateDurationExpressionBehavior);
        break;
      case ExpressionKinds.TimeSpan:
        this.clearExpressionBehaviorRange(object.expressionBehavior as ITimeSpanExpressionBehavior);
        break;
      case ExpressionKinds.DateComparison:
        this.clearExpressionBehaviorContents(object.expressionBehavior as IDateComparisonExpressionBehavior);
        break;
    }
  }

  private clearCalculationExpressionBehaviorContents(behavior: ICalculationExpressionBehavior): void {
    this.clearExpressionBehaviorContents(behavior);
    behavior.conditionalExpressionBehavior.contents = [];
  }

  private clearExpressionBehaviorContents<T>(behavior: IExpressionContainable<T>): void {
    behavior.contents = [];
  }

  private clearExpressionBehaviorRange<T>(behavior: IExpressionRangeSpecifiable<T>): void {
    behavior.from = null;
    behavior.to = null;
  }

  private clearTextJoinBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfITextJoinBehaviorSpecifiable(object)) {
      return;
    }
    object.textJoinBehavior.items = [];
  }

  private clearGraphBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIGraphObject(object)) {
      return;
    }
    switch (object.kind) {
      case GraphKinds.Line:
        this.clearLineGraphBehaviorContents(object.behavior as ILineGraphBehavior);
        break;
    }
  }

  private clearLineGraphBehaviorContents<T>(behavior: ILineGraphBehavior): void {
    behavior.items = [];
  }

  private clearTranscribeBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfITranscribeBehaviorSpecifiable(object)) {
      return;
    }
    object.transcribeBehavior.targets = [];
  }

  private clearDateCalculateBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIDateCalculateBehaviorSpecifiable(object)) {
      return;
    }
    object.dateCalculateBehavior.items = [];
  }

  private clearItemSelectTriggerBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIItemSelectTriggerBehaviorSpecifiable(object)) {
      return;
    }
    object.itemSelectTriggerBehavior.targets = [];
  }

  private clearTranscribeDateOfTimeBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfITranscribeDateOfTimeBehaviorSpecifiable(object)) {
      return;
    }
    object.transcribeDateOfTimeBehavior.isEnable = false;
    object.transcribeDateOfTimeBehavior.target = null;
  }

  private clearEmptyAlertAllowBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIEmptyAlertAllowBehaviorSpecifiable(object)) {
      return;
    }
    object.emptyAlertAllowBehavior.targets = [];
  }

  private clearGroupAlertAllowBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIGroupAlertAllowBehaviorSpecifiable(object)) {
      return;
    }
    object.groupAlertAllowBehavior.targets = [];
  }

  private clearScriptExecuteTriggerBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIScriptExecuteTriggerBehaviorSpecifiable(object)) {
      return;
    }
    object.scriptExecuteTriggerBehavior.targets = [];
  }

  private clearScriptExecuteBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIScriptExecuteBehaviorSpecifiable(object)) {
      return;
    }
    const behavior = object.scriptExecuteBehavior as ILinkedScriptExecuteBehavior;
    if (!behavior) { return; }
    behavior.references = [];
  }

  private clearHospitalizedHisInformationItemSelectBehaviorObjectReferences(object: IEditableFormatObject): void {
    if (!instanceOfIHospitalizedHisInformationItemSelectBehaviorSpecifiable(object)) {
      return;
    }
    object.hospitalizedHisInformationItemSelectBehavior.baseDateReferBehavior.isEnable = false;
    object.hospitalizedHisInformationItemSelectBehavior.baseDateReferBehavior.reference = null;
  }

  setClippedObjects(objects: IEditableFormatObject[]): void {
    localStorage.setItem(EditableFormatClipboardRepository.objectsClipperUuid, this.uuid);
    localStorage.setItem(EditableFormatClipboardRepository.clippedObjectsKey, this.serializeClippedObjects(Array.isArray(objects) ? objects : []));
  }

  private serializeClippedObjects(objects: IEditableFormatObject[]): string {
    return JSON.stringify(this._translatorFactory.create().toIXElementContentsObjectsBean(objects, null));
  }

  private static readonly objectsClipperUuid = "objectsClipperUuid";
  private static readonly clippedObjectsKey = "clippedObjectsKey";
}
